import { z } from 'zod';

import { CompanyStatus } from '../admin/companies';
import { IsoDateTimeSchema } from '../common/datetime';
import { TimezoneSchema } from '../common/fields';
import { CompanyId, SiteId, TerminalId, TerminalSessionId } from '../common/ids';

import { BootstrapTokenSchema } from './qr';

// ---------------------------------------------------------------------------
// Estado del canal (data-model.md · "Estado del canal (TerminalState)"; contracts/terminal-api.md)
// ---------------------------------------------------------------------------

/**
 * Modo calculado por el servidor. `CONNECTING`, `OFFLINE` y `AUTONOMOUS` existen solo en el
 * cliente (`MarkerState` de `apps/marker`), nunca viajan por el canal.
 */
export const TerminalModeSchema = z.enum(['STANDBY', 'PAUSED', 'DISABLED', 'REVOKED']);
export type TerminalMode = z.infer<typeof TerminalModeSchema>;

/**
 * Motivo de revocación de una sesión de terminal (`terminal_sessions.revoke_reason`). Viaja en el
 * mensaje `revoked` del canal y en `details.reason` de `401 TERMINAL_SESSION_REVOKED`.
 */
export const TERMINAL_REVOKE_REASONS = ['REPLACED', 'UNLINKED', 'SITE_INACTIVE'] as const;
export const TerminalRevokeReasonSchema = z.enum(TERMINAL_REVOKE_REASONS);
export type TerminalRevokeReason = z.infer<typeof TerminalRevokeReasonSchema>;

/**
 * `COMPANY_SUSPENDED` ⇒ `PAUSED`; `ADMIN` | `SITE_INACTIVE` ⇒ `DISABLED`;
 * `REPLACED` | `UNLINKED` | `SITE_INACTIVE` ⇒ `REVOKED`; `null` ⇒ `STANDBY`.
 */
export const TerminalStateReasonSchema = z
  .enum(['COMPANY_SUSPENDED', 'ADMIN', 'SITE_INACTIVE', 'REPLACED', 'UNLINKED'])
  .nullable();
export type TerminalStateReason = z.infer<typeof TerminalStateReasonSchema>;

/**
 * `bootstrap_enabled = mode === 'STANDBY'`;
 * `autonomous_allowed = mode === 'STANDBY' && company.autonomous_mode_enabled && keyDelivered && !keyRevoked`.
 * `key_version` es la versión vigente de la clave de terminal (`autonomous_key_version`, ≥ 1).
 */
export const TerminalStateSchema = z.object({
  mode: TerminalModeSchema,
  reason: TerminalStateReasonSchema,
  bootstrap_enabled: z.boolean(),
  autonomous_allowed: z.boolean(),
  key_version: z.number().int().min(1),
});
export type TerminalState = z.infer<typeof TerminalStateSchema>;

// ---------------------------------------------------------------------------
// Configuración entregada al marcador (research R-15: nada se hardcodea en el cliente)
// ---------------------------------------------------------------------------

/** Valores de `TerminalConfig.reconnect` fijados por contracts/terminal-api.md (research R-1). */
export const TERMINAL_RECONNECT_DEFAULTS = {
  base_ms: 1000,
  max_ms: 30_000,
  fallback_after_failures: 2,
  fallback_window_ms: 30_000,
} as const;

export const TerminalReconnectConfigSchema = z.object({
  base_ms: z.number().int().positive(),
  max_ms: z.number().int().positive(),
  fallback_after_failures: z.number().int().positive(),
  fallback_window_ms: z.number().int().positive(),
});
export type TerminalReconnectConfig = z.infer<typeof TerminalReconnectConfigSchema>;

/**
 * `enabled`/`after_seconds` vienen de `companies.autonomous_mode_enabled` /
 * `autonomous_mode_after_seconds` (001); `slot_ms` y `max_sync_age_hours` de `AUTONOMOUS_SLOT_MS`
 * y `AUTONOMOUS_MAX_SYNC_AGE_HOURS` (env). Los rangos los valida `config/env.ts`, no este esquema.
 */
export const TerminalAutonomousConfigSchema = z.object({
  enabled: z.boolean(),
  after_seconds: z.number().int().positive(),
  slot_ms: z.number().int().positive(),
  max_sync_age_hours: z.number().int().positive(),
});
export type TerminalAutonomousConfig = z.infer<typeof TerminalAutonomousConfigSchema>;

/**
 * `TerminalConfig` de contracts/terminal-api.md. `TERMINAL_ACTIVATION_RATE_LIMIT_PER_MIN` es solo
 * del servidor y **no** forma parte de esta configuración (research R-15).
 */
export const TerminalConfigSchema = z.object({
  heartbeat_seconds: z.number().int().positive(),
  offline_after_seconds: z.number().int().positive(),
  bootstrap_rotation_seconds: z.number().int().positive(),
  autonomous: TerminalAutonomousConfigSchema,
  reconnect: TerminalReconnectConfigSchema,
});
export type TerminalConfig = z.infer<typeof TerminalConfigSchema>;

// ---------------------------------------------------------------------------
// `GET /me` del realm terminal (`TerminalMe`; también dentro de `hello_ack` y `POST /activate`)
// ---------------------------------------------------------------------------

export const TerminalMeSessionSchema = z.object({
  id: TerminalSessionId,
  started_at: IsoDateTimeSchema,
});
export type TerminalMeSession = z.infer<typeof TerminalMeSessionSchema>;

export const TerminalMeTerminalSchema = z.object({
  id: TerminalId,
  name: z.string(),
});
export type TerminalMeTerminal = z.infer<typeof TerminalMeTerminalSchema>;

export const TerminalMeSiteSchema = z.object({
  id: SiteId,
  name: z.string(),
  timezone: TimezoneSchema,
});
export type TerminalMeSite = z.infer<typeof TerminalMeSiteSchema>;

export const TerminalMeCompanySchema = z.object({
  id: CompanyId,
  trade_name: z.string(),
  status: CompanyStatus,
});
export type TerminalMeCompany = z.infer<typeof TerminalMeCompanySchema>;

/**
 * Responde también con empresa `SUSPENDED`, sede `INACTIVE` o terminal deshabilitado: no hay
 * guarda de estado en el realm terminal (FR-019/FR-021); el estado viaja en `state`.
 */
export const TerminalMeSchema = z.object({
  session: TerminalMeSessionSchema,
  terminal: TerminalMeTerminalSchema,
  site: TerminalMeSiteSchema,
  company: TerminalMeCompanySchema,
  state: TerminalStateSchema,
  config: TerminalConfigSchema,
  server_time: IsoDateTimeSchema,
});
export type TerminalMe = z.infer<typeof TerminalMeSchema>;

// ---------------------------------------------------------------------------
// Bootstrap Codes entregados por adelantado (research R-7)
// ---------------------------------------------------------------------------

/**
 * Un slot de rotación: `slot = floor(server_time_ms / (BOOTSTRAP_ROTATION_SECONDS * 1000))`,
 * `bt` = token de 20 caracteres crockford32 (research R-6). El marcador nunca fabrica un `bt`.
 */
export const BootstrapSlotSchema = z.object({
  slot: z.number().int().nonnegative(),
  bt: BootstrapTokenSchema,
  starts_at: IsoDateTimeSchema,
  ends_at: IsoDateTimeSchema,
});
export type BootstrapSlot = z.infer<typeof BootstrapSlotSchema>;

/** Slot actual + los 3 siguientes (4 en total, horizonte 60–80 s con rotación de 20 s). */
export const BOOTSTRAP_BATCH_SLOTS = 4;

export const BootstrapBatchSchema = z.object({
  rotation_seconds: z.number().int().positive(),
  slots: z.array(BootstrapSlotSchema).min(1),
});
export type BootstrapBatch = z.infer<typeof BootstrapBatchSchema>;
